// Prompt Golf (Review 19, D53): scoring that both the Worker and the browser use. A stroke is one
// helper message; fewer strokes (then fewer words, then less time) is better. Nothing here knows the
// holes' hidden checks (those live in @buildlab/assignments/golf, server only).

/** Words in a message: runs of non-space characters with at least one letter, digit or emoji. */
export function countWords(text: string): number {
  return String(text ?? "")
    .split(/\s+/)
    .filter((w) => /[\p{L}\p{N}\p{Extended_Pictographic}]/u.test(w)).length;
}

export type ParKind = "ace" | "under" | "par" | "over";

export interface ParLabel {
  /** "Hole in one!", "Birdie", "Par", "Bogey", "+4"… */
  label: string;
  /** Strokes minus par (negative is under par). */
  diff: number;
  kind: ParKind;
}

const UNDER: Record<number, string> = { [-1]: "Birdie", [-2]: "Eagle", [-3]: "Albatross" };
const OVER: Record<number, string> = { 1: "Bogey", 2: "Double bogey", 3: "Triple bogey" };

/** The golf name for a score on a hole. One stroke is always a hole in one. */
export function parLabel(strokes: number, par: number): ParLabel {
  const diff = strokes - par;
  if (strokes === 1) return { label: "Hole in one!", diff, kind: "ace" };
  if (diff === 0) return { label: "Par", diff, kind: "par" };
  if (diff < 0) return { label: UNDER[diff] ?? `${diff}`, diff, kind: "under" };
  return { label: OVER[diff] ?? `+${diff}`, diff, kind: "over" };
}

/** A finished round, for ranking. */
export interface GolfScore {
  strokes: number;
  words: number;
  /** How long the round took, start to finish. */
  seconds: number;
  /** When it finished (the earlier of two identical scores ranks first). */
  finishedAt: number;
}

/** Fewer strokes, then fewer words, then less time, then whoever got there first. */
export function compareScores(a: GolfScore, b: GolfScore): number {
  return a.strokes - b.strokes || a.words - b.words || a.seconds - b.seconds || a.finishedAt - b.finishedAt;
}

/** Each person's best round (key → best), from all their finished rounds. */
export function bestPerPlayer<T extends GolfScore>(rounds: T[], key: (r: T) => string): T[] {
  const best = new Map<string, T>();
  for (const r of rounds) {
    const k = key(r);
    const cur = best.get(k);
    if (!cur || compareScores(r, cur) < 0) best.set(k, r);
  }
  return [...best.values()];
}

/** Sorted best-first with a rank; identical strokes, words and seconds share a rank. */
export function rankScores<T extends GolfScore>(rounds: T[]): (T & { rank: number })[] {
  const sorted = [...rounds].sort(compareScores);
  const out: (T & { rank: number })[] = [];
  sorted.forEach((r, i) => {
    const prev = out[i - 1];
    const tie = prev && prev.strokes === r.strokes && prev.words === r.words && prev.seconds === r.seconds;
    out.push({ ...r, rank: tie ? prev.rank : i + 1 });
  });
  return out;
}

/** "1:05" for 65 seconds. */
export function clockTime(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${rest}` : `${m}:${rest}`;
}
